import type { User } from '../types';

export type AppEnvironment = 'development' | 'staging' | 'testserver' | 'production' | 'unknown';

const PRODUCTION_HOSTS = new Set(['plattform.etappio.se', 'platform.etappio.se']);
const STAGING_HOSTS = new Set(['staging.etappio.se']);
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]', '::1']);

/**
 * The environment the web app runs in, from the address it is served on.
 * The same build runs everywhere and no deploy variable says which
 * environment it is, so the hostname is the one source that cannot be set
 * wrong at deploy.
 */
export function environmentForHost(hostname: string): AppEnvironment {
  const host = hostname.trim().toLowerCase();
  if (PRODUCTION_HOSTS.has(host)) return 'production';
  if (STAGING_HOSTS.has(host)) return 'staging';
  if (LOCAL_HOSTS.has(host) || host.endsWith('.localhost')) return 'development';
  return 'unknown';
}

/** Staff, or an admin impersonating a user, count as internal accounts. */
export function isInternalAccount(user: Pick<User, 'isAdmin' | 'isSupport' | 'impersonation'>): boolean {
  return Boolean(user.isAdmin || user.isSupport || user.impersonation);
}

/**
 * Everyone signed in sees the indicator in development and staging. In
 * production, and on an address we do not recognise, only internal accounts
 * do: real end users never see it.
 */
export function showsEnvironmentIndicator(
  environment: AppEnvironment,
  user: Pick<User, 'isAdmin' | 'isSupport' | 'impersonation'> | null | undefined,
  testModeEnabled = false
): boolean {
  // Signed out, the bar only shows while the server runs the test mode (it
  // replaces the old "Lokalt testläge" row), never in production.
  if (!user) return testModeEnabled && environment !== 'production';
  if (environment === 'development' || environment === 'staging' || environment === 'testserver') return true;
  return isInternalAccount(user) || (environment === 'production' && testModeEnabled);
}

/**
 * How much of the test mode a build may offer here: everything in
 * development, a limited panel (integrations locked to the real test
 * services) in staging and on the test server. Production requires explicit
 * account authorization from the server and keeps integrations locked. An unrecognised address counts as
 * production unless the server confirms that the test mode runs.
 */
export type TestModeAccess = 'full' | 'limited' | 'locked';

export function testModeAccess(environment: AppEnvironment, testModeEnabled: boolean): TestModeAccess {
  if (environment === 'development') return 'full';
  if (environment === 'staging' || environment === 'testserver') return 'limited';
  if (environment === 'unknown' && testModeEnabled) return 'limited';
  if (environment === 'production' && testModeEnabled) return 'limited';
  return 'locked';
}
